import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Divider,
  Grid,
  Chip,
  Box,
  Tooltip,
  IconButton,
  FormControlLabel,
} from "@mui/material";
import { GetSingleKnowledgeBaseResponse } from "../../Models/knowledge";
import color from "../../Constants/colors";
import { useState } from "react";
import DocumentList from "./DocumentList/DocumentList";
import { KnowledgeBaseStatus } from "../../Enums/companyStatus";
import CloseIcon from "@mui/icons-material/Close";
import { formatDate } from "../../Helpers/dateHelpers";
import { getStatusTextColor } from "../../Helpers/evaluationHelpers";
import { statusToStringKnowledgeBase } from "../../Helpers/stringHelpers";
import {
  downloadDocumentBlob,
  isPreviewableFile,
} from "../../Helpers/documentHelper";
import { useDownloadDocument } from "../../Query/useCompanies";
import {
  useAddKnowledgeBaseDocuments,
  useDeleteKnowledgeBaseDocument,
  useKnowledgeBaseDocuments,
} from "../../Query/useKnowledge";
import FileUploader from "../FileUpload/FileUploader";
import { ComoSwitch } from "../Common/ComoSwitch";
import { allowedExtensionsKnowledge } from "../../Constants/fileExtensions";
import { hasRole } from "../../Helpers/authHelpers";
import { UserRole } from "../../Enums/companyStatus";
import { Pipeline } from "../../Enums/pipeline";
import { useTranslation } from "react-i18next";
import ImagePreviewModal from "../Common/ImagePreviewModal";
import PdfPreviewModal from "../Common/PdfPreviewModal";
import LogViewerModal from "../FileUpload/LogViewer";

const KnowledgeBaseModal = ({
  open,
  onClose,
  knowledgeBase,
  isLoading,
  onStopKnowledgeBase,
  refetchKnowledgeBase,
}: {
  open: boolean;
  onClose: () => void;
  knowledgeBase: GetSingleKnowledgeBaseResponse | null;
  isLoading: boolean;
  onStopKnowledgeBase: (id: number) => void;
  refetchKnowledgeBase: () => void;
}) => {
  const { t } = useTranslation("modal");
  const [isStopping, setIsStopping] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [pipeline, setPipeline] = useState<Pipeline>(Pipeline.LEGACY);
  const isAdmin = hasRole(UserRole.ADMIN);
  const downloadDocumentMutation = useDownloadDocument();
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    number | undefined
  >(undefined);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<number | null>(
    null,
  );
  const [previewDocumentName, setPreviewDocumentName] = useState<string>("");
  const knowledgeBaseId = knowledgeBase?.id ?? 0;
  const { data: documents } = useKnowledgeBaseDocuments(
    knowledgeBaseId,
    open && !!knowledgeBaseId,
  );
  const addDocumentsMutation = useAddKnowledgeBaseDocuments(knowledgeBaseId);
  const deleteDocumentMutation =
    useDeleteKnowledgeBaseDocument(knowledgeBaseId);

  const documentsData = documents ?? knowledgeBase?.documents ?? [];

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handlePreviewDocument = (documentId: number, fileName: string) => {
    if (!isPreviewableFile(fileName)) return;

    setPreviewDocumentId(documentId);
    setPreviewDocumentName(fileName);

    if (fileName.toLowerCase().endsWith(".pdf")) {
      setPdfPreviewOpen(true);
    } else {
      setImagePreviewOpen(true);
    }
  };

  const handleViewLogs = (documentId: number) => {
    setSelectedDocumentId(documentId);
    setLogModalOpen(true);
  };

  const handleDeleteDocuments = (ids: number[]) => {
    setSelectedIds([]);

    ids.forEach((id) => {
      deleteDocumentMutation.mutate(id);
    });
  };

  const handleRemoveNewFile = (fileName: string) => {
    setNewFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleOpenNewFile = (file: File) => {
    window.open(URL.createObjectURL(file));
  };

  const handleUpload = () => {
    if (newFiles.length === 0) return;
    addDocumentsMutation.mutate(
      { files: newFiles, pipeline },
      {
        onSuccess: () => setNewFiles([]),
      },
    );
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const handleDownloadDocument = (documentId: number, fileName: string) => {
    downloadDocumentMutation.mutate(
      { documentId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, fileName);
        },
      },
    );
  };

  const handleStopAndRefetch = (id: number) => {
    setIsStopping(true);
    onStopKnowledgeBase(id);
    setTimeout(() => {
      refetchKnowledgeBase();
      setIsStopping(false);
    }, 3000);
  };

  if (!knowledgeBase) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent
        sx={{
          overflowY: "auto",
          padding: "2rem",
          color: color.primary_dark,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                color: color.primary_dark,
                fontWeight: "bold",
              }}
            >
              {knowledgeBase.name}
            </Typography>
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                sx={{
                  color: color.primary_dark,
                  fontSize: "0.8rem",
                  marginTop: "5px",
                }}
              >
                {t("created_at")} {formatDate(knowledgeBase.created_at)}
              </Typography>
              <Typography
                sx={{
                  color: color.primary_dark,
                  fontSize: "0.8rem",
                  marginTop: "5px",
                }}
              >
                {t("updated_at")} {formatDate(knowledgeBase.updated_at)}
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography
                  sx={{
                    color: color.primary_dark,
                    fontSize: "0.8rem",
                    marginTop: "5px",
                  }}
                >
                  <span>
                    {t("status")}{" "}
                    <span
                      style={{
                        opacity: 1,
                        fontWeight: "bold",
                        color: getStatusTextColor(knowledgeBase.status),
                      }}
                    >
                      {knowledgeBase.status === KnowledgeBaseStatus.IN_PROGRESS
                        ? t("indexing")
                        : statusToStringKnowledgeBase(knowledgeBase.status)}
                    </span>
                  </span>
                </Typography>
                {knowledgeBase?.status === KnowledgeBaseStatus.IN_PROGRESS && (
                  <>
                    <CircularProgress
                      size={18}
                      sx={{
                        marginLeft: "10px",
                        color: color.primary_dark,
                        verticalAlign: "middle",
                      }}
                    />
                    <Tooltip title={t("stop_knowledge_base")}>
                      <IconButton
                        sx={{ color: color.accent, verticalAlign: "middle" }}
                        disabled={
                          isStopping ||
                          knowledgeBase.status !==
                            KnowledgeBaseStatus.IN_PROGRESS
                        }
                        onClick={() => handleStopAndRefetch(knowledgeBase.id)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: color.primary_dark,
                fontWeight: "bold",
              }}
            >
              {t("description")}
            </Typography>
            <Divider sx={{ backgroundColor: color.primary_dark }} />
            <Typography
              variant="body2"
              sx={{
                margin: "10px 0",
                fontSize: "1rem",
                fontWeight: "normal",
                color: color.primary_dark,
              }}
            >
              {knowledgeBase.description}
            </Typography>
          </Grid>
        </Grid>
        {knowledgeBase.used_in.length > 0 && (
          <>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: color.primary_dark,
                fontWeight: "bold",
                marginTop: "2rem",
              }}
            >
              {t("used_in_categories")}
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              {Array.from(
                new Set(knowledgeBase.used_in.map((item) => item.name)),
              ).map((name, index) => (
                <Chip
                  key={index}
                  sx={{
                    color: color.white,
                    backgroundColor: color.accent,
                    fontWeight: "bold",
                  }}
                  label={name}
                  variant="outlined"
                />
              ))}
            </Box>
          </>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "1.5rem",
          }}
        >
          <Typography
            variant="body1"
            gutterBottom
            sx={{ color: color.primary_dark, fontWeight: "bold" }}
          >
            {t("documents")}
          </Typography>
          {selectedIds.length > 0 && (
            <Button
              size="small"
              onClick={() => handleDeleteDocuments(selectedIds)}
              disabled={deleteDocumentMutation.isPending}
              sx={{
                color: color.white,
                backgroundColor: color.accent,
                "&:hover": {
                  backgroundColor: color.primary_dark,
                  color: color.accent,
                },
              }}
            >
              {t("delete_selected", { count: selectedIds.length })}
            </Button>
          )}
        </Box>
        <Divider sx={{ backgroundColor: color.primary_dark, mb: 1 }} />

        <DocumentList
          documentsData={documentsData}
          onDownloadDocument={handleDownloadDocument}
          isDownloading={downloadDocumentMutation.isPending}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onDeleteDocument={(id) => handleDeleteDocuments([id])}
          isDeleting={deleteDocumentMutation.isPending}
          onPreviewDocument={handlePreviewDocument}
          onViewLogs={handleViewLogs}
        />

        <Box sx={{ marginTop: "1rem" }}>
          <FileUploader
            uploadedFiles={newFiles}
            setUploadedFiles={setNewFiles}
            allowedExtensions={allowedExtensionsKnowledge}
            page={0}
            rowsPerPage={50}
            handleRemoveFile={handleRemoveNewFile}
            handleOpenFile={handleOpenNewFile}
            disabled={addDocumentsMutation.isPending}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            {isAdmin ? (
              <FormControlLabel
                control={
                  <ComoSwitch
                    checked={pipeline === Pipeline.MISTRAL}
                    onChange={(e) =>
                      setPipeline(
                        e.target.checked ? Pipeline.MISTRAL : Pipeline.LEGACY,
                      )
                    }
                    disabled={addDocumentsMutation.isPending}
                  />
                }
                label={t("use_mistral_pipeline")}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.8rem",
                    color: color.primary_dark,
                  },
                }}
              />
            ) : (
              <span />
            )}
            <Button
              onClick={handleUpload}
              disabled={newFiles.length === 0 || addDocumentsMutation.isPending}
              sx={{
                color: color.white,
                backgroundColor: color.accent,
                "&:hover": {
                  backgroundColor: color.primary_dark,
                  color: color.accent,
                },
              }}
            >
              {addDocumentsMutation.isPending ? (
                <CircularProgress size={18} sx={{ color: color.white }} />
              ) : (
                t("upload_documents", { count: newFiles.length })
              )}
            </Button>
          </Box>
        </Box>
        <LogViewerModal
          open={logModalOpen}
          onClose={() => setLogModalOpen(false)}
          documentId={selectedDocumentId}
          useKb={true}
        />
        <PdfPreviewModal
          open={pdfPreviewOpen}
          onClose={() => setPdfPreviewOpen(false)}
          documentId={previewDocumentId}
          documentName={previewDocumentName}
        />
        <ImagePreviewModal
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          documentId={previewDocumentId}
          documentName={previewDocumentName}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: color.accent,
            color: color.white,
            marginBottom: "10px",
            marginTop: "10px",
            "&:hover": {
              backgroundColor: color.primary_dark,
              color: color.accent,
            },
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnowledgeBaseModal;
